import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  InGameCurrency,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes.ts';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import {
  Horizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BodyL, BrandL } from '@sorare/core/src/atoms/typography/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';
import {
  REWARD_STORY_IMAGE_PX,
  rewardImageFromUrl,
} from '@sorare/core/src/components/rewards/RewardUnlockedAnimation/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';
import {
  getRewardImage,
  mergeRewardsConfig,
} from '@sorare/core/src/lib/rewards.tsx';
import { SetBrandingImage } from '@sorare/core/src/set/branding/SetBrandingImage.tsx';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { DumbRewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/DumbRewardUnlockedStory.tsx';
import { MultiRewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/MultiRewardUnlockedStory/index.tsx';
import { ButtonsWrapper } from '#src/components/missions/stories/RewardUnlockedStory/styles.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import type {
  ClaimWelcomeBackRewards_cardSet,
  ClaimWelcomeBackRewards_currentUser,
} from './__generated__/index.graphql.ts';

const Content = styled(Horizontal).attrs({ center: true })`
  position: relative;
  padding: 0 var(--double-unit) calc(10 * var(--unit));
  height: 100%;

  @media ${laptopAndAbove} {
    min-height: min(
      calc(90 * var(--unit)),
      calc(var(--100vh) - 8 * var(--unit))
    );
  }
`;

type Props = {
  currentUser: ClaimWelcomeBackRewards_currentUser;
  cardSet?: Nullable<ClaimWelcomeBackRewards_cardSet>;
};
export const ClaimWelcomeBackRewards = ({ currentUser, cardSet }: Props) => {
  const { setEndOfSeasonTasks } = currentUser;
  const [initialClaimableTasks] = useState(
    setEndOfSeasonTasks.filter(
      task => task.aasmState === ManagerProgressionTaskState.COMPLETED
    )
  );

  const commonGameSet = useCommonGameSet();
  const { sport } = useSportContext();

  const [open, setOpen] = useState(!!initialClaimableTasks.length);
  const [showRewardDialog, setShowRewardDialog] = useState(false);

  const logoUrl = cardSet?.branding?.setLogo;
  const [claimTask] = useClaimTask();

  const mergedRewardConfigs = mergeRewardsConfig(
    initialClaimableTasks.flatMap(task => task.rewardConfigs)
  );

  const inGameCurrencyRewardConfig = mergedRewardConfigs.find(
    isTypeFn('InGameCurrencyRewardConfig')
  );
  const gemRewardConfig =
    !!inGameCurrencyRewardConfig &&
    inGameCurrencyRewardConfig.currency === InGameCurrency.COMMON_GEM
      ? inGameCurrencyRewardConfig
      : undefined;
  const essenceRewardConfig = mergedRewardConfigs.find(
    isTypeFn('CardShardRewardConfig')
  );

  const showDualReward = !!gemRewardConfig && !!essenceRewardConfig;
  const showSingleReward = !!essenceRewardConfig && !showDualReward;

  const essenceTitle = (
    <BrandL>
      <FormattedMessage
        defaultMessage="{setName} Essence"
        values={{ setName: commonGameSet.name }}
      />
    </BrandL>
  );
  const essenceSubtitle = (
    <BodyL>
      <FormattedMessage defaultMessage="Here is your reward to start playing the new set!" />
    </BodyL>
  );
  const gemTitle = (
    <BrandL>
      <FormattedMessage defaultMessage="Gems" />
    </BrandL>
  );
  const gemSubtitle = (
    <BodyL>
      <FormattedMessage defaultMessage="Use these gems to build your first lineup!" />
    </BodyL>
  );

  const claimAllTasks = async () => {
    try {
      await Promise.all(
        initialClaimableTasks.map(async task =>
          claimTask({
            variables: {
              taskId: task.id,
              sport,
            },
          })
        )
      );
      setShowRewardDialog(true);
      if (!mergedRewardConfigs.length) {
        setOpen(false);
      }
    } catch {
      setOpen(false);
    }
  };
  const onClose = () => {
    setOpen(false);
  };

  const backgroundUrl =
    cardSet?.branding?.welcomeBackBackgroundMobile ??
    cardSet?.branding?.rewardSuccessPopupMobile;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      style={{
        background: backgroundUrl
          ? `url(${backgroundUrl}) center / cover no-repeat`
          : undefined,
      }}
    >
      {showRewardDialog ? (
        <>
          {!!showSingleReward && (
            <DumbRewardUnlockedStory
              images={[
                getRewardImage(essenceRewardConfig, REWARD_STORY_IMAGE_PX),
              ]
                .filter(Boolean)
                .map(rewardImageFromUrl)}
              amount={essenceRewardConfig.quantity.toString()}
              title={essenceTitle}
              subtitle={essenceSubtitle}
              onClose={onClose}
              hideBackground
            />
          )}
          {!!showDualReward && (
            <MultiRewardUnlockedStory
              onClose={onClose}
              hideBackground
              groups={[
                {
                  title: essenceTitle,
                  subtitle: essenceSubtitle,
                  images: [
                    getRewardImage(essenceRewardConfig, REWARD_STORY_IMAGE_PX),
                  ]
                    .filter(Boolean)
                    .map(rewardImageFromUrl),
                  amount: essenceRewardConfig.quantity.toString(),
                },
                {
                  title: gemTitle,
                  subtitle: gemSubtitle,
                  images: [
                    getRewardImage(gemRewardConfig, REWARD_STORY_IMAGE_PX),
                  ]
                    .filter(Boolean)
                    .map(rewardImageFromUrl),
                  amount: gemRewardConfig.amount.toString(),
                },
              ]}
            />
          )}
        </>
      ) : (
        <Content>
          <Vertical center gap={5} className="text-center">
            <SetBrandingImage
              url={logoUrl}
              height={48}
              color="var(--c-white)"
            />
            <Vertical center gap={2}>
              <BrandL color="var(--c-white)">
                <FormattedMessage
                  defaultMessage="New season.{br}New Trophy."
                  values={{
                    br: <br />,
                  }}
                />
              </BrandL>
              <BodyL color="var(--c-white)">
                <FormattedMessage
                  defaultMessage="Your performance last season has earned you a head start in the {setName} field."
                  values={{ setName: commonGameSet.name }}
                />
              </BodyL>
            </Vertical>
          </Vertical>
          <ButtonsWrapper>
            <Button size="large" color="primary" onClick={claimAllTasks}>
              <FormattedMessage defaultMessage="Claim my rewards" />
            </Button>
          </ButtonsWrapper>
        </Content>
      )}
    </Dialog>
  );
};

ClaimWelcomeBackRewards.fragments = {
  currentUser: gql`
    fragment ClaimWelcomeBackRewards_currentUser on CurrentUser {
      slug
      setEndOfSeasonTasks(sport: $sport) {
        id
        aasmState
        rewardConfigs {
          id
          ...getRewardImage_anyRewardConfigInterface
          ...mergeRewardsConfig_anyRewardConfigInterface
        }
      }
    }
    ${getRewardImage.fragments.anyRewardConfigInterface}
    ${mergeRewardsConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ClaimWelcomeBackRewards_currentUser>,
  cardSet: gql`
    fragment ClaimWelcomeBackRewards_cardSet on CardSet {
      slug
      branding {
        id
        setLogo: assetUrl(slot: BRANDING_SET_LOGO_COMPOSITE_MOBILE)
        welcomeBackBackgroundMobile: assetUrl(
          slot: ONBOARDING_LOYALTY_REWARD_WELCOME_BACK_OFFER_MOBILE
        )
        rewardSuccessPopupMobile: assetUrl(
          slot: PLAY_REWARD_SUCCES_POPUP_MOBILE
        )
      }
    }
  ` as TypedDocumentNode<ClaimWelcomeBackRewards_cardSet>,
};
